/**
 * @file CareersPage.jsx
 * @description Official careers page for SimpleCode. Showcases company culture, 
 *              core values, and upcoming opportunities in a clean, Bento-inspired light theme.
 * @module components/CareersPage
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Navbar from './layout/Navbar';
import Footer from './layout/Footer';
import {
    FiZap,
    FiHeart,
    FiStar,
    FiCode,
    FiChevronDown,
    FiChevronRight,
    FiArrowRight,
    FiBriefcase
} from 'react-icons/fi';

/* ══════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component CareersPage
 * @description Renders the platform's careers and culture showcase.
 *
 * @param {Object} props
 * @param {Function} props.navigate - Router navigation function.
 * @returns {JSX.Element} The rendered Careers page.
 */
const CareersPage = ({ navigate }) => {
    const { isAuthenticated } = useAuth();
    // ── Component State ──
    const [expandedValue, setExpandedValue] = useState(null);
    const [loading, setLoading] = useState(true);

    /* ══════════════════════════════════════════════════════════════
     * DATA & CONFIG
     * ══════════════════════════════════════════════════════════════ */

    // ── Navigation Links (Synchronized across the platform) ──
    const navLinks = [
        { id: "home", label: "Home", action: () => navigate("/") },
        { id: "learning", label: "Learning", action: () => navigate("/learning") },
        { id: "dashboard", label: "Dashboard", action: () => navigate("/dashboard"), show: isAuthenticated },
        { id: "dsa", label: "DSA Problems", action: () => navigate("/dsa-problems") },
        { id: "system-design", label: "System Design", action: () => navigate("/system-design") }
    ].filter(link => link.show !== false);

    const footerLinks = [
        { label: "Home", action: () => navigate("/") },
        { label: "DSA Problems", action: () => navigate("/dsa-problems") },
        { label: "System Design", action: () => navigate("/system-design") }
    ];

    useEffect(() => {
        window.scrollTo(0, 0);
        document.title = "SimpleCode | Careers";
        // Emulate a light loading state for smooth entry
        const timer = setTimeout(() => setLoading(false), 400);
        return () => {
            clearTimeout(timer);
            document.title = "SimpleCode";
        };
    }, []);

    /**
     * @constant values
     * @description Core company values displayed in the accordion.
     * @type {Array<{icon: JSX.Element, title: string, content: string}>}
     */
    const values = [
        {
            icon: <FiCode />,
            title: 'Innovation First',
            content: 'We push technical and pedagogical boundaries to create better learning experiences. We believe that coding education should be as dynamic as the technology it teaches.'
        },
        {
            icon: <FiStar />,
            title: 'Impact Driven',
            content: 'Every line of code we ship helps someone learn better. We measure our success by the success of our students and the growth of our learning community.'
        },
        {
            icon: <FiZap />,
            title: 'Move Fast',
            content: 'We ship quickly and iterate based on real feedback. We value agility and the ability to adapt to the ever-changing landscape of software engineering.'
        }
    ];

    if (loading) {
        return (
            <div className="min-h-screen bg-accent flex flex-col font-sans">
                <Navbar navigate={navigate} navLinks={navLinks} />
                <main className="flex-1 pt-16">
                    <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16 animate-pulse mt-8">
                        <div className="h-10 w-64 bg-emerald-100 rounded mb-8" />
                        <div className="bg-white rounded-lg p-8 shadow-sm space-y-6">
                            <div className="h-48 w-full bg-gray-50 rounded mb-8" />
                            {[...Array(3)].map((_, i) => (
                                <div key={i} className="border-b border-gray-100 pb-4">
                                    <div className="h-6 w-1/3 bg-gray-100 rounded mb-2" />
                                    <div className="h-4 w-full bg-gray-50 rounded" />
                                </div>
                            ))}
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-accent text-foreground flex flex-col font-sans">
            <Navbar navigate={navigate} navLinks={navLinks} />

            <main className="flex-1 pt-16">

                {/* ── Careers Header ── */}
                <section className="bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 mb-8 mt-4 border-b border-emerald-100/30">
                    <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16 py-8">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <h1 className="text-left text-3xl font-bold text-foreground underline decoration-dotted decoration-foreground underline-offset-8">
                                SimpleCode <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent underline decoration-dotted decoration-emerald-600 ml-1">Careers</span>
                            </h1>
                            <p className="text-muted-foreground text-sm font-medium text-left">Join the Family</p>
                        </div>
                    </div>
                </section>

                <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16 pb-20">
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">

                        {/* ── Left Side: Sidebar Cards (Sticky on Desktop) ── */}
                        <div className="lg:col-span-1 lg:sticky lg:top-24 lg:self-start space-y-6 pb-12">
                            <div className="bg-white border-l-4 border-emerald-600 rounded p-6 shadow-sm relative overflow-hidden">
                                <div className="relative z-10">
                                    <h2 className="text-lg font-bold text-emerald-600 mb-3 flex items-center justify-center">
                                        Our Mission
                                    </h2>
                                    <p className="text-muted-foreground text-xs leading-relaxed text-left text-justify">
                                        We're on a mission to democratize coding education.
                                        We believe in building tools that empower individuals to transform their careers and lives through software engineering.
                                    </p>
                                </div>
                            </div>

                            <div className="flex-col items-center justify-between p-6 bg-emerald-600 rounded text-white shadow-md space-y-4">
                                <div>
                                    <h4 className="text-lg font-bold">Stay Updated</h4>
                                    <p className="text-emerald-50 text-xs text-justify my-2">Be the first to know about new openings and community roles. Career Opportunities will be posted here soon.</p>
                                </div>
                                <button
                                    onClick={() => navigate('/support')}
                                    className="w-full justify-center bg-white text-emerald-600 px-5 py-3 rounded font-bold text-xs hover:bg-emerald-50 transition-all flex items-center gap-2"
                                >
                                    GET IN TOUCH <FiArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {/* ── Right Side: Substantive Content (Main Scroll) ── */}
                        <div className="lg:col-span-3">
                            <div className="bg-white border-l-4 border-emerald-600 rounded overflow-hidden relative">

                                {/* Background Watermark */}
                                <div className="hidden md:block absolute right-[-20px] top-[-20px] opacity-[0.15] transform rotate-12 transition-opacity duration-300 pointer-events-none z-20">
                                    <FiBriefcase className="w-72 h-72" />
                                </div>

                                <div className="relative z-10">
                                    {/* Hero Section */}
                                    <div className="p-8 border-b border-border-border">
                                        <h2 className="text-2xl font-bold text-foreground mb-4">
                                            Build the Future of <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">Coding Education</span>
                                        </h2>
                                        <p className="text-muted-foreground text-sm leading-relaxed text-justify">
                                            At SimpleCode, we're a passionate team of developers, designers, and educators working together to create the best platform for aspiring coders.
                                            If you share our vision for a community-driven learning future, we'd love to hear from you.
                                        </p>
                                    </div>

                                    {/* Interactive Values */}
                                    <div className="divide-y divide-border-border">
                                        {values.map((value, index) => {
                                            const isExpanded = expandedValue === index;
                                            return (
                                                <div key={index} className="group transition-colors duration-200 hover:bg-emerald-50/60">
                                                    <button
                                                        onClick={() => setExpandedValue(isExpanded ? null : index)}
                                                        className="w-full flex items-center justify-between p-6 text-left focus:outline-none"
                                                    >
                                                        <div className="flex items-center gap-4 hover:text-emerald-600">
                                                            <span className="text-sm font-bold text-emerald-600 group-hover:text-emerald-600 transition-colors w-6 flex items-center justify-center">
                                                                {React.cloneElement(value.icon, { className: "w-5 h-5" })}
                                                            </span>
                                                            <h3 className={`text-base font-bold transition-colors group-hover:text-emerald-600 ${isExpanded ? 'text-emerald-700' : 'text-foreground'}`}>
                                                                {value.title}
                                                            </h3>
                                                        </div>
                                                        {isExpanded ? (
                                                            <FiChevronDown className="w-5 h-5 text-emerald-600" />
                                                        ) : (
                                                            <FiChevronRight className="w-5 h-5 text-gray-400 group-hover:text-emerald-600" />
                                                        )}
                                                    </button>

                                                    <div
                                                        className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
                                                    >
                                                        <div className="overflow-hidden">
                                                            <div className="px-16 pb-6 text-sm text-muted-foreground leading-relaxed text-justify">
                                                                {value.content}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <Footer navigate={navigate} navLinks={footerLinks} />
        </div>
    );
};

export default CareersPage;
